<template>
  <div>
    <StepCard title="Titular da Conta" subtitle="Responsável pela solicitação" class="mb-6">
      <v-list-item class="px-0 py-0">
        <template v-slot:prepend>
          <v-avatar color="grey-lighten-1">
            <v-icon color="white">mdi-account-circle</v-icon>
          </v-avatar>
        </template>
        <v-list-item-title class="font-weight-medium">{{ props.titular?.nome }}</v-list-item-title>
        <v-list-item-subtitle>CPF: {{ props.titular?.matricula?.cpf }}</v-list-item-subtitle>
      </v-list-item>
    </StepCard>

    <StepCard title="Beneficiário do Procedimento" subtitle="Selecione para quem foi o procedimento.">
      <v-select label="Selecione o familiar" :items="listaBeneficiarios" :loading="props.familiaresLoading"
        :disabled="props.familiaresLoading" item-title="nome" item-value="handle"
        v-model="beneficiarioSelecionadoHandle" variant="outlined" return-object :error-messages="props.error"
        @update:modelValue="emitirBeneficiario" no-data-text="Nenhum familiar encontrado."></v-select>

      <div v-if="formData.beneficiarioHandle" class="mt-6">
        <v-divider class="mb-6"></v-divider>
        <v-row no-gutters>
          <v-col cols="12" class="py-2 mb-2">
            <span class="font-weight-medium text-subtitle-1">
              Possui o número da Protocolo De Atendimento Autorizado?
              <span v-if="hasAtendimento === null" class="text-error">*</span>
            </span>
          </v-col>

          <v-col cols="12" class="d-flex ga-4">
            <v-btn :variant="hasAtendimento === true ? 'elevated' : 'outlined'"
              :color="hasAtendimento === true ? 'primary' : 'grey'" @click="setHasAtendimento(true)">Sim</v-btn>
            <v-btn :variant="hasAtendimento === false ? 'elevated' : 'outlined'"
              :color="hasAtendimento === false ? 'primary' : 'grey'" @click="setHasAtendimento(false)">Não</v-btn>
          </v-col>
        </v-row>

        <v-expand-transition>
          <div v-if="hasAtendimento !== null">
            <v-row no-gutters class="mt-4" v-if="hasAtendimento === true">
              <v-col cols="12" md="6">
                <v-select ref="protocoloSelectRef" v-model="modelAtendimento" label="Número do Protocolo de Atendimento Autorizado"
                  placeholder="Selecione o Protocolo de Atendimento Autorizado" :items="props.solicitacoes"
                  item-title="protocoloAtendimento" item-value="atendimento" :loading="props.solicitacoesLoading"
                  :disabled="props.solicitacoesLoading" variant="outlined" density="compact" hide-details
                  :rules="atendimentoRules" required></v-select>
              </v-col>
            </v-row>
            <div v-else-if="hasAtendimento === false" class="mt-5">
              <v-alert type="info" variant="tonal" density="compact">
                <div class="text-body-2">
                  O contato prévio com a Operadora para busca de rede credenciada e auxílio na disponibilização de vaga para o atendimento desejado, respeitando os prazos regulatórios, assegura e confere agilidade ao seu pedido de reembolso.
                  <br><br>
                  Esclarecemos que a rede credenciada especializada pode ser consultada no site da <a href="https://www.cassems.com.br" target="_blank">CASSEMS</a> ou através da Central de Atendimento 24h pelo telefone/WhatsApp: (67) 3314-1010.
                </div>
              </v-alert>
            </div>
          </div>
        </v-expand-transition>
      </div>

      <v-expand-transition>
        <div v-if="formData.beneficiarioHandle" class="mt-6">
          <DetalhesBeneficiarioCard :beneficiario="formData" />
        </div>
      </v-expand-transition>
    </StepCard>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import DetalhesBeneficiarioCard from './DetalhesBeneficiarioCard.vue';
import StepCard from './StepCard.vue';

const formData = defineModel({
  type: Object,
  required: true,
});

const modelAtendimento = defineModel('atendimento', {
  type: [Object, String, Number],
  default: null,
});

const props = defineProps({
  titular: Object,
  familiares: Array,
  familiaresLoading: Boolean,
  error: String,
  solicitacoes: { type: Array, default: () => [] },
  solicitacoesLoading: Boolean,
});

const emit = defineEmits(['update:beneficiario-selecionado']);

const beneficiarioSelecionadoHandle = ref(null);

const protocoloSelectRef = ref(null);

const hasAtendimento = ref(null);

const listaBeneficiarios = computed(() => {
  const lista = [...props.familiares];
  if (props.titular?.handle && !lista.some(f => f.handle === props.titular.handle)) {
    lista.unshift(props.titular);
  }
  return lista;
});

const atendimentoRules = computed(() => {
  const rules = [];

  if (hasAtendimento.value === true) {
    rules.push(v => (v !== null && v !== undefined) || 'O número do protocolo de atendimento é obrigatório.');
  }

  return rules;
});

const setHasAtendimento = (hasValue) => {
  hasAtendimento.value = hasValue;

  if (hasValue === false) {
    modelAtendimento.value = null;
  }
};

const emitirBeneficiario = (beneficiario) => {
  emit('update:beneficiario-selecionado', beneficiario);
};

watch(() => formData.value.beneficiarioHandle, (newHandle) => {
  if (newHandle && !beneficiarioSelecionadoHandle.value) {
    beneficiarioSelecionadoHandle.value = listaBeneficiarios.value.find(f => f.handle === newHandle);
  }
}, { immediate: true });

watch(beneficiarioSelecionadoHandle, () => {
  modelAtendimento.value = null;
});

watch(() => modelAtendimento.value, (valor) => {
  if (valor) {
    hasAtendimento.value = true;
  }
}, { immediate: true });

const validate = async () => {
  if (hasAtendimento.value === null) {
    return false;
  }

  if (hasAtendimento.value === true && protocoloSelectRef.value) {
    const validation = await protocoloSelectRef.value.validate();
    if (validation.length > 0) return false;
  }

  return true;
};

defineExpose({ validate });
</script>